/**
 * The one desktop runner for the whole app, and its settings on this machine.
 *
 * It lives here rather than in a screen so it keeps taking work while the person is on any page —
 * or has the window hidden in the tray. The settings are this machine's, so they stay in local
 * storage; nothing about them goes to the server.
 */

import { readLanes } from './agentLanes'
import { contextText, readLocalInstructions, withEveryPrompt } from './localInstructions'
import type { LocalAgentId, Permission } from './localAgent'
import { showPreview } from './preview'
import { PUBLISHED_EVENT, type PublishOutcome } from './publish'
import { useSyncExternalStore } from 'react'
import { api, API_BASE } from './api'
import { parseOpenCodeLine } from './chatProtocol'
import { AgentReporter, RunReport } from './agentTelemetry'
import { desktopMachineName, isDesktop, skillsList, powerKeepAwake, repoChanges, repoCheckpoint, repoRestoreCheckpoint, repoRestoreFile, repoFiles, repoStatus, tunnelClose, tunnelList, tunnelOpen, agentFetch, agentKeyRef, outboxRead, outboxWrite, worktreeBring, worktreeCommit, worktreeDiscard, worktreeFor } from './desktop'
import { agentProgram, providerSlug, runAgent } from './localAgent'
import { LocalRunner, type RepoRunnerStatus, type RunnerSettings } from './localRunner'
import { allFoldersByRepository, folderFor } from './repoFolders'

const SETTINGS_KEY = 'devpulse.localRunner'
const MACHINE_KEY = 'devpulse.machineId'

export const DEFAULT_SETTINGS: RunnerSettings = { enabled: false, agent: 'claude', permission: 'edits', repositories: [] }

export function readRunnerSettings(): RunnerSettings {
  try {
    const stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null') as Partial<RunnerSettings> | null
    return { ...DEFAULT_SETTINGS, ...(stored ?? {}) }
  } catch {
    return DEFAULT_SETTINGS
  }
}

/** This computer's id among the person's machines (the queue's "@casa"); used to aim work at it ("Quem faz?"). */
export function thisMachineId(): string {
  return machineId()
}

function machineId(): string {
  try {
    let id = localStorage.getItem(MACHINE_KEY)
    if (!id) {
      id = crypto.randomUUID().slice(0, 8)
      localStorage.setItem(MACHINE_KEY, id)
    }
    return id
  } catch {
    return 'desktop'
  }
}

let runner: LocalRunner | null = null

function instance(): LocalRunner {
  if (runner) return runner
  const settings = () => readRunnerSettings()
  runner = new LocalRunner({
    apiBase: API_BASE,
    // The agent key stays in the app: these calls go through it with a marker (lib/desktop.ts, agentFetch).
    fetch: agentFetch,
    key: agentKeyRef,
    folderFor,
    machineId: machineId(),
    machineName: desktopMachineName,
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    // What a run brings up on this machine opens beside the conversation (docs/refactor/11-preview.md).
    open: (url) => showPreview(url),
    // Publicar: the tunnels are the app's (src-tauri/src/tunnels.rs); the main one also opens in the preview.
    publish: async (services) => {
      const outcome: PublishOutcome = { published: [], refused: [] }
      for (const service of services) {
        try {
          const shared = await tunnelOpen(service.port, service.name)
          outcome.published.push({ name: service.name, url: shared.url, role: service.role, main: service.main })
        } catch (error) {
          outcome.refused.push({ name: service.name, reason: String(error) })
        }
      }
      window.dispatchEvent(new CustomEvent(PUBLISHED_EVENT, { detail: outcome }))
      return outcome
    },
    worktreeFor: worktreeFor,
    worktreeCommit: worktreeCommit,
    worktreeDiscard: worktreeDiscard,
    worktreeBring: worktreeBring,
    unpublish: async () => {
      const shared = await tunnelList()
      await tunnelClose()
      return shared.length
    },
    // A command with no project ran in this one: its window comes up (docs/refactor/29).
    openProject: (name) => void import('./projectWindows').then((m) => m.showProject(name)),
    status: repoStatus,
    reporter: sharedReporter(),
    provider: () => providerSlug(settings().agent),
    agent: () => settings().agent,
    failover: () => settings().failover ?? [],
    pinned: pinnedText,
    branch: async (folder) => (await repoFiles(folder)).branch,
    checkpoint: repoCheckpoint,
    changes: repoChanges,
    restore: repoRestoreCheckpoint,
    restoreFile: repoRestoreFile,
    run: (prompt, folder, onLine, options) => {
      const { agent: chosen, permission: allowed } = settings()
      // A teammate's command never gets more than editing the project's files (docs/refactor/63).
      const permission = options?.fromTeammate ? 'edits' : allowed
      // "@codex …": another agent for this one run.
      const agent = options?.agent ?? chosen
      // Either Claude account is Claude Code (docs/refactor/60): the same stream, session and system prompt.
      const claude = agentProgram(agent) === 'claude'
      // A conversation turn streams its events (Claude Code); other agents print plain lines.
      const conversation = options?.conversation && claude ? options.conversation : undefined
      let stop: (() => Promise<void>) | null = null
      let stopAsked = false
      // The run's id is also the agent's session id (agents.rs): the server ends that agent's sessions with it.
      const sessionId = conversation?.id ?? crypto.randomUUID()
      const done = new Promise<{ output: string; code: number | null }>((resolve, reject) => {
        const lines: string[] = []
        // What goes with every call: Claude Code as an appended system prompt; others, before the prompt.
        void instructionsFor(folder, options?.withProject === true).then((system) => {
        const sent = system && !claude ? `${system}\n\n---\n\n${prompt}` : prompt
        runAgent(sessionId, agent, folder, sent, permission, {
          onLine: (line, stream) => {
            if (stream === 'stdout') lines.push(line)
            onLine(line, stream)
          },
          onExit: (code) => resolve({ output: lines.join('\n'), code }),
        }, conversation ? { sessionId: conversation.id, resume: conversation.resume } : undefined, { reportedByApp: options?.reported === true, system: claude ? system : undefined, model: settings().models?.[agent] })
          .then((stopRun) => {
            stop = stopRun
            // ■ pressed while the agent was still starting: it stops as soon as it exists.
            if (stopAsked) void stopRun()
          })
          .catch(reject)
        }, reject)
      })
      return {
        done,
        stop: () => {
          stopAsked = true
          if (stop) void stop()
        },
        sessionId,
        streaming: Boolean(conversation) || agent === 'opencode',
        parse: agent === 'opencode' ? parseOpenCodeLine : undefined,
      }
    },
  })
  return runner
}

/**
 * What goes with every call from this machine (docs/refactor/21-steering-local-e-compartilhado.md): the
 * project's pinned instructions (when asked here), this machine's own, and the skills on in this
 * folder. Empty when there is nothing.
 */
export async function instructionsFor(folder: string, withProject: boolean): Promise<string> {
  const [project, skills, context] = await Promise.all([
    withProject ? pinnedText().catch(() => '') : Promise.resolve(''),
    skillsList(folder).catch(() => []),
    vephusContext(folder).catch(() => ''),
  ])
  return withEveryPrompt({
    context,
    project,
    local: readLocalInstructions(),
    skills: skills.filter((s) => s.enabled).map((s) => ({ name: s.name, description: s.description })),
  }).text
}

/**
 * "Você está no Vephus" (docs/refactor/19-ia-sabe-onde-esta.md): who, which project, this computer and
 * the person's other computers taking commands — kept for a minute, like the pinned instructions.
 */
let contextCache: { folder: string; permission: Permission; text: string; at: number } | null = null
async function vephusContext(folder: string): Promise<string> {
  // Read every time: a change in Conectar agente reaches the next request, not the one a minute later.
  const permission = readRunnerSettings().permission
  if (contextCache && contextCache.folder === folder && contextCache.permission === permission && Date.now() - contextCache.at < 60_000) return contextCache.text
  const [me, machines, name] = await Promise.all([
    api.auth.me().catch(() => null),
    api.promptQueue.machines().catch(() => []),
    desktopMachineName().catch(() => null),
  ])
  const here = machineId()
  const project = Object.entries(allFoldersByRepository()).find(([, path]) => path === folder)?.[0] ?? null
  const principal = machines.find((m) => m.principal)
  const text = contextText({
    person: me?.displayName ?? null,
    project,
    machine: name,
    otherMachines: machines.filter((m) => m.id !== here && m.online && m.takingCommands && m.name).map((m) => (m.principal ? `${m.name} (o principal)` : m.name!)),
    principal: Boolean(principal && principal.id === here),
    principalName: principal && principal.id !== here ? principal.name ?? null : null,
    agentsHere: readLanes().length,
    permission,
  })
  contextCache = { folder, permission, text, at: Date.now() }
  return text
}

// The pinned instructions, kept for a minute: a conversation starts without waiting on the server.
let pinnedCache: { text: string; at: number } | null = null
async function pinnedText(): Promise<string> {
  if (pinnedCache && Date.now() - pinnedCache.at < 60_000) return pinnedCache.text
  try {
    const { text } = await api.docs.pinnedText()
    pinnedCache = { text: text.trim(), at: Date.now() }
  } catch {
    // Offline: the last ones known, or none.
  }
  return pinnedCache?.text ?? ''
}

/**
 * Runs a prompt the person gave on this machine, now, in that agent's conversation (rodar aqui):
 * no queue. Desktop only; rejects 'no-folder' when the repository has no folder here.
 */
let reporterInstance: AgentReporter | null = null
function sharedReporter(): AgentReporter {
  reporterInstance ??= new AgentReporter({ apiBase: API_BASE, fetch: agentFetch, key: agentKeyRef, outbox: { load: outboxRead, save: outboxWrite } })
  return reporterInstance
}

/**
 * The reporter's outbox (docs/refactor/52), started once on the desktop: what an earlier run of the app could not send
 * goes now, and anything kept is tried every two minutes. Null in a browser, which reports nothing itself.
 */
let outboxStarted = false
export function telemetryOutbox(): AgentReporter | null {
  if (!isDesktop()) return null
  const reporter = sharedReporter()
  if (!outboxStarted) {
    outboxStarted = true
    reporter.start()
  }
  return reporter
}

/**
 * A report for one turn of the "IA desta máquina" chat (docs/refactor/01-rodar-aqui.md): the same as a
 * prompt run here — the prompt, each tool, the tokens, the answer — into the chat conversation's
 * session. Null in a browser.
 */
export function chatTurnReport(repository: string, conversationId: string): RunReport | null {
  if (!isDesktop()) return null
  const agent = readRunnerSettings().agent
  return new RunReport(sharedReporter(), providerSlug(agent), repository, conversationId)
}

export function runHere(repository: string, prompt: string, lane = 1, agent?: LocalAgentId, shown?: string) {
  return instance().runHere(repository, prompt, lane, agent, shown)
}

/** Takes back a request given here that still waits its turn (the × above the prompt box). */
export function withdrawWaiting(repository: string, lane: number, id: string): boolean {
  return isDesktop() ? instance().withdraw(repository, lane, id) : false
}

/** Forgets a lane's last answer (the dock's "limpar"). */
export function clearResult(repository: string, lane = 1) {
  if (isDesktop()) instance().clearResult(repository, lane)
}

/** Closing an extra agent removes its own checkout (ADR-0011); its branch, with every turn, stays. */
export function discardLane(repository: string, lane: number): Promise<boolean> {
  return isDesktop() ? instance().discardLane(repository, lane) : Promise.resolve(false)
}

/** Ends an agent's conversation here: the next prompt starts a new one. */
export function newConversation(repository: string, lane = 1) {
  if (isDesktop()) instance().newConversation(repository, lane)
}

/** A command was queued for this person: the runner asks for it now. */
export function pokeRunner() {
  if (isDesktop() && runner) runner.poke()
}

/** Fetches the pinned instructions ahead of the first prompt (app start). */
export function warmPinned() {
  if (isDesktop()) void pinnedText()
}

/** Saves the settings and starts or stops the runner to match. */
export function saveRunnerSettings(settings: RunnerSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
  } catch {
    // Private storage: the runner still follows these settings until the app closes.
  }
  if (isDesktop()) {
    instance().apply(settings)
    holdAwake(settings)
  }
}

/** Taking work, the computer does not sleep — unless the person said it may (docs/refactor/28). */
function holdAwake(settings: RunnerSettings) {
  void powerKeepAwake(settings.enabled && settings.keepAwake !== false).catch(() => {})
}

/** Starts the runner at launch if it was on. Called once, from the app's root. */
export function startRunnerIfOn() {
  if (!isDesktop()) return
  const settings = readRunnerSettings()
  if (settings.enabled) instance().apply(settings)
  holdAwake(settings)
}

/**
 * Runs `work` alone in `folder`, after whatever the runner is running there — so the desktop chat and
 * the runner never have two agents in one folder. In a browser there is no runner: it just runs.
 */
export function runExclusive<T>(folder: string, work: () => Promise<T>): Promise<T> {
  return isDesktop() ? instance().exclusive(folder, work) : work()
}

/** Whether the runner has an agent running in `folder` now (desktop only). */
/** Stops this desktop's agent working in `repository` (and lane) right now, if any; false in a browser. */
export function stopRunnerFor(repository: string, lane = 1): boolean {
  return isDesktop() ? instance().stopRepository(repository, lane) : false
}

export function folderBusy(folder: string): boolean {
  return isDesktop() && runner !== null ? runner.busy(folder) : false
}

const NOTHING: RepoRunnerStatus[] = []

// Stable functions, defined once: a subscribe created per render makes React resubscribe on every
// render, and with a fresh snapshot each time that is an endless loop.
const subscribeToRunner = (listener: () => void) => (isDesktop() ? instance().subscribe(listener) : () => {})
const runnerSnapshot = () => (isDesktop() ? instance().snapshot() : NOTHING)

/** What the runner is doing now, for a screen. Empty in a browser. */
export function useRunnerStatus(): RepoRunnerStatus[] {
  return useSyncExternalStore(subscribeToRunner, runnerSnapshot)
}
